import { useTranslations } from "@zapier/i18n";
import { ChangeEvent, MouseEvent } from "react";
import { Icon } from "@zapier/zinnia-icons";
import { Field, TextInput, VisuallyHidden } from "@zapier/design-system";
import { commitSearchOnEnter } from "../../../../utils/commitSearchOnEnter";
import styles from "./MoveModal.module.css";

interface Props {
  searchQuery: string;
  setSearchQuery: (searchQuery: string) => void;
  clearSearch: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Flushes a pending result announcement when focus leaves the field early. */
  onSearchBlur?: () => void;
  /** Announces the current results straight away when the user commits with Enter. */
  onSearchCommit?: () => void;
}

export function FolderFilter({
  searchQuery,
  setSearchQuery,
  clearSearch,
  onSearchBlur,
  onSearchCommit,
}: Props) {
  const t = useTranslations("folder-filter");

  return (
    <div className={styles.search}>
      <Field
        label={t("labels.search-folders")}
        inputId="move-folder-filter-folders"
        renderLabel={({ htmlFor, label }) => (
          <VisuallyHidden>
            <label htmlFor={htmlFor}>{label}</label>
          </VisuallyHidden>
        )}
        renderInput={(inputProps) => (
          <TextInput
            // `id` comes from {...inputProps}; Field derives it from the
            // `inputId` prop above, which is the single source of truth that
            // also drives the label's htmlFor association.
            {...inputProps}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- intentionally focus the filter input when the modal opens
            autoFocus={true}
            type="search"
            renderIconBefore={({ iconSize }) => (
              <Icon name="navSearch" size={iconSize} />
            )}
            renderIconAfter={({ iconSize, isFocused, isHovered }) => {
              const iconColor =
                isFocused || isHovered ? undefined : "GrayWarm6";
              return searchQuery ? (
                <button
                  type="button"
                  className={styles.clearButton}
                  onClick={clearSearch}
                  aria-label={t("aria-labels.clear-search")}
                >
                  <Icon
                    canAcceptPointerEvents={false}
                    color={iconColor}
                    name="formX"
                    size={iconSize}
                  />
                </button>
              ) : undefined;
            }}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const value = event.target?.value;
              setSearchQuery(value);
            }}
            onBlur={() => {
              onSearchBlur?.();
            }}
            onKeyDown={commitSearchOnEnter(() => onSearchCommit?.())}
            placeholder={t("labels.search-placeholder")}
            value={searchQuery}
          />
        )}
      />
    </div>
  );
}
